import React, { useRef, useCallback, useEffect, useState } from 'react'
import { withStyles } from '@material-ui/core/styles'
import { useDispatch } from 'react-redux'
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
} from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import { Icon } from '@iconify/react'
import { CoreButton } from 'src/components/core'
import TextFieldPopup from 'src/components/textfield-popup'
import SuggestionSkeleton from 'src/components/suggestion-skeleton'
import { aICreatorSuggestGoalAction } from 'src/reducers/ai-creator/action'
import { isMobile } from 'react-device-detect'
import styles from './styles'

const PageGoals = ({
  classes,
  topic = '',
  attachmentIds = [],
  selectionKey,
  goals,
  onGoalsChange,
}) => {
  const dispatch = useDispatch()
  const [bulkLoading, setBulkLoading] = useState(false)
  const [popupStates, setPopupStates] = useState({})
  const [anchorElements, setAnchorElements] = useState({})
  const [popupLoadingStates, setPopupLoadingStates] = useState({})
  const [fieldSuggestions, setFieldSuggestions] = useState({})
  const goalRefs = useRef({})
  const presentedAllRef = useRef([])
  const presentedPerFieldRef = useRef({})
  const lastFetchedKeyRef = useRef(null)

  const attachmentKey = attachmentIds.join(',')
  const hasSource = !!topic?.trim() || attachmentIds.length > 0

  const ensureAtLeastOne = arr => (arr.length === 0 ? [''] : arr)

  const updateGoal = (index, value) => {
    const next = [...goals]
    next[index] = value
    onGoalsChange(next)
  }

  const addGoal = () => {
    onGoalsChange([...goals, ''])
  }

  const deleteGoal = index => {
    const next = goals.filter((_, i) => i !== index)
    const ensured = ensureAtLeastOne(next)
    onGoalsChange(ensured)
    // Sync contentEditable nodes with new array
    setTimeout(() => {
      ensured.forEach((g, i) => {
        if (goalRefs.current[i] && goalRefs.current[i].textContent !== g) {
          goalRefs.current[i].textContent = g
        }
      })
      Object.keys(goalRefs.current).forEach(k => {
        if (Number(k) >= ensured.length) delete goalRefs.current[k]
      })
    }, 0)
  }

  const buildRequestData = (extraPresented = []) => ({
    topic: topic || '',
    currentGoals: goals.filter(g => g && g.trim() !== ''),
    attachmentIds,
    presentGoals: [
      ...presentedAllRef.current,
      ...extraPresented,
    ],
  })

  const fetchBulkSuggestions = useCallback(() => {
    if (!hasSource) return

    setBulkLoading(true)
    dispatch(
      aICreatorSuggestGoalAction(buildRequestData(), response => {
        const allSuggestions =
          response && response.suggestedGoals ? response.suggestedGoals : []
        // Tüm öneriler presented'a kaydedilir (regenerate'te duplicate gelmesin),
        // ama listede sadece 1 tanesi görünür — kullanıcı "Add More Goals" ile
        // daha fazlasını ekleyebilir.
        presentedAllRef.current = [
          ...presentedAllRef.current,
          ...allSuggestions,
        ]
        const nextGoals = ensureAtLeastOne(allSuggestions.slice(0, 1))
        onGoalsChange(nextGoals)
        setTimeout(() => {
          nextGoals.forEach((g, idx) => {
            if (goalRefs.current[idx]) {
              goalRefs.current[idx].textContent = g
            }
          })
          Object.keys(goalRefs.current).forEach(k => {
            if (Number(k) >= nextGoals.length) delete goalRefs.current[k]
          })
        }, 0)
        setBulkLoading(false)
      }),
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topic, attachmentKey, hasSource, dispatch, onGoalsChange])

  const fetchFieldSuggestions = useCallback(
    fieldIndex => {
      if (!hasSource) return

      const previousField = presentedPerFieldRef.current[fieldIndex] || []
      setPopupLoadingStates(prev => ({ ...prev, [fieldIndex]: true }))

      dispatch(
        aICreatorSuggestGoalAction(
          buildRequestData(previousField),
          response => {
            const suggestions =
              response && response.suggestedGoals
                ? response.suggestedGoals
                : []
            presentedPerFieldRef.current = {
              ...presentedPerFieldRef.current,
              [fieldIndex]: [...previousField, ...suggestions],
            }
            setFieldSuggestions(prev => ({
              ...prev,
              [fieldIndex]: suggestions,
            }))
            setPopupLoadingStates(prev => ({ ...prev, [fieldIndex]: false }))
          },
        ),
      )
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [topic, attachmentKey, hasSource, dispatch, goals],
  )

  // Auto-fetch bulk goals when source selection changes (debounced)
  useEffect(() => {
    if (!hasSource || !selectionKey) return
    if (lastFetchedKeyRef.current === selectionKey) return
    const timer = setTimeout(() => {
      lastFetchedKeyRef.current = selectionKey
      presentedAllRef.current = []
      presentedPerFieldRef.current = {}
      setFieldSuggestions({})
      fetchBulkSuggestions()
    }, 600)
    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectionKey, hasSource])

  const handleGoalFocus = index => {
    if (goals[index] && goals[index].trim() !== '') return
    setPopupStates(prev => ({ ...prev, [index]: true }))
    setAnchorElements(prev => ({
      ...prev,
      [index]: goalRefs.current[index],
    }))
    if (fieldSuggestions[index] && fieldSuggestions[index].length > 0) return
    if (hasSource) fetchFieldSuggestions(index)
  }

  const handlePopupClose = index => {
    setPopupStates(prev => ({ ...prev, [index]: false }))
  }

  const handleSuggestionClick = (index, suggestion) => {
    updateGoal(index, suggestion)
    if (goalRefs.current[index]) {
      goalRefs.current[index].textContent = suggestion
    }
    setFieldSuggestions(prev => ({ ...prev, [index]: null }))
    setTimeout(() => handlePopupClose(index), 50)
  }

  const handleKeyDown = (e, index) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      addGoal()
      return
    }
    if (
      e.key === 'Backspace' &&
      (!goals[index] || goals[index].trim() === '') &&
      goals.length > 1
    ) {
      e.preventDefault()
      deleteGoal(index)
    }
  }

  return (
    <div className={classes.container}>
      <div className={classes.header}>
        <Typography className={classes.headerTitle}>Page goals</Typography>
        <CoreButton
          size="small"
          variant="text"
          color="secondary"
          startIcon={
            bulkLoading ? (
              <CircularProgress size={14} />
            ) : (
              <Icon icon="hugeicons:exchange-01" fontSize={16} />
            )
          }
          onClick={fetchBulkSuggestions}
          disabled={!hasSource || bulkLoading}
        >
          {bulkLoading ? 'Generating...' : 'Regenerate'}
        </CoreButton>
      </div>
      <div className={classes.goalsList}>
        {bulkLoading ? (
          <SuggestionSkeleton />
        ) : (
          goals.map((goal, index) => {
            const isFieldLoading = popupLoadingStates[index] || false
            const suggestions = fieldSuggestions[index]
            return (
              <Box key={index} className={classes.goalItem}>
                <Typography className={classes.goalNumber}>
                  {index + 1}.
                </Typography>
                <div
                  ref={el => {
                    goalRefs.current[index] = el
                  }}
                  contentEditable
                  suppressContentEditableWarning
                  className={classes.goalText}
                  data-placeholder="Type a goal or select from suggested goals"
                  onBlur={() => {
                    const goalText =
                      goalRefs.current[index]?.textContent || ''
                    if (goal !== goalText) {
                      updateGoal(index, goalText)
                    }
                  }}
                  onFocus={() => handleGoalFocus(index)}
                  onKeyDown={e => handleKeyDown(e, index)}
                  style={{ cursor: 'text' }}
                >
                  {goal}
                </div>

                <TextFieldPopup
                  anchorEl={anchorElements[index]}
                  open={popupStates[index] || false}
                  onClose={() => handlePopupClose(index)}
                  placement="bottom-start"
                  offset={4}
                  maxHeight={250}
                >
                  <div className={classes.popupContent}>
                    <div className={classes.popupTitleContainer}>
                      <Typography
                        fontSize={14}
                        fontWeight={400}
                        className={classes.popupTitle}
                      >
                        Suggested page goals:
                      </Typography>
                      {suggestions && suggestions.length > 0 && (
                        <CoreButton
                          size="small"
                          variant="text"
                          color="secondary"
                          startIcon={
                            isFieldLoading ? (
                              <CircularProgress size={16} />
                            ) : (
                              <Icon
                                icon="hugeicons:exchange-01"
                                fontSize={16}
                              />
                            )
                          }
                          onClick={() => fetchFieldSuggestions(index)}
                          disabled={isFieldLoading || !hasSource}
                        >
                          {isFieldLoading ? 'Generating...' : 'Regenerate'}
                        </CoreButton>
                      )}
                    </div>
                    <List dense className={classes.suggestionList}>
                      {isFieldLoading ? (
                        <SuggestionSkeleton />
                      ) : suggestions && suggestions.length > 0 ? (
                        suggestions.map((suggestion, suggestionIndex) => (
                          <ListItem
                            key={suggestionIndex}
                            button
                            className={classes.suggestionItem}
                            onClick={() =>
                              handleSuggestionClick(index, suggestion)
                            }
                          >
                            <div className={classes.suggestionItemIcon}>
                              <img
                                src="/assets/AI-Assistant-icon.svg"
                                width={isMobile ? 16 : 24}
                                height={isMobile ? 16 : 24}
                                alt="AI Assistant"
                              />
                            </div>
                            <ListItemText
                              primary={suggestion}
                              primaryTypographyProps={{
                                fontSize: 14,
                                fontWeight: 400,
                                className: classes.suggestionText,
                              }}
                            />
                          </ListItem>
                        ))
                      ) : (
                        <div
                          style={{ textAlign: 'center', padding: '20px' }}
                        >
                          <Typography
                            variant="body2"
                            style={{ color: '#637381' }}
                          >
                            {hasSource
                              ? 'No suggestions available. Try regenerating.'
                              : 'Select a source to get suggestions.'}
                          </Typography>
                        </div>
                      )}
                    </List>
                  </div>
                </TextFieldPopup>

                <CloseIcon
                  fontSize="small"
                  className={classes.closeIcon}
                  onClick={() => deleteGoal(index)}
                  style={{
                    visibility: goals.length === 1 ? 'hidden' : 'visible',
                  }}
                />
              </Box>
            )
          })
        )}
        <CoreButton
          variant="text"
          color="secondary"
          size="small"
          className={classes.addButton}
          startIcon={<Icon icon="lets-icons:add-round" />}
          onClick={addGoal}
          disabled={bulkLoading}
        >
          Add More Goals
        </CoreButton>
      </div>
    </div>
  )
}

export default withStyles(styles)(PageGoals)
